import { Box, Heading, HStack, Spacer, VStack } from "@chakra-ui/layout";
import {
  Flex,
  Icon,
  Tab,
  TabList,
  Tabs,
  Tag,
  TagLeftIcon,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import {
  RiClipboardLine,
  RiExternalLinkLine,
  RiLightbulbLine,
} from "react-icons/ri";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { NoDataTable } from "src/components/Feedback/NoDataTable";
import { SearchInput } from "src/components/Inputs/SearchInput";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { WithFeatureFlags } from "src/components/Permissions/WithFeatureFlags";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useFormTemplates } from "src/components/Providers/FormTemplateProvider";
import {
  AttendanceTypes,
  FormTabsTypes,
  SearchAndFilterTypes,
} from "src/constants";
import { EnrollmentPeriodIdProvider } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import { useFormsList } from "src/scenes/orgAdmin/forms/components/useFormsList";
import * as breadcrumb from "src/services/breadcrumb";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { ActivityButton } from "./activity/ActivityButton";
import { CreateFormsButton } from "./components/CreateFormsButton";
import { FormListViewWithFilter } from "./components/FormListViewWithFilter";
import { ImportMenuInput } from "./components/ImportMenuInput";
import { NoForms } from "./components/NoForms";
import { TableHeader } from "./components/TableHeader";
import { DropoffFormsList } from "./DropoffFormsList";
import { FormFilters } from "./formFilters/FormFilters";
import { FormsList } from "./FormsList";
import { ResizableFilterPanel } from "./ResizableFilterPanel";

export const Forms = (): React.ReactElement | null => {
  const { isElevatedSchoolAdmin, hasApplyingSchools, hasAttendingSchools } =
    useSchoolAdmin();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const organization = useOrganization();
  const {
    filter,
    search,
    enrollmentPeriodId,
    formsList,
    tabs,
    remoteData,
    pagination,
    setPagination,
    selection,
    setSelection,
  } = useFormsList();
  const { selectedNavFormTemplate } = useFormTemplates();
  const flags = useFlags(["filter-drawer"]);
  const filterDrawerEnabled = flags["filter-drawer"].enabled;

  React.useEffect(() => {
    const state = location.state as { clearBulkSelection?: boolean } | null;
    if (!state?.clearBulkSelection) {
      return;
    }
    setSelection((s) => s.clear());
    navigate(".", { replace: true, state: {} });
  }, [location.state, navigate, setSelection]);

  const { setAttendance } = formsList;
  React.useEffect(() => {
    if (!isElevatedSchoolAdmin) {
      if (searchParams.has(SearchAndFilterTypes.Attendance)) {
        setAttendance("");
      }
      return;
    }

    // There is some messy logic here because permissions are all temporarily
    // granted while initially evaluting them, which means that we cannot rely
    // on their initial values to be correct.  So we can assign an initial value
    // to the attendance filter if unset, but we also have to correct it if
    // those permissions change.
    const attendance = searchParams.get(SearchAndFilterTypes.Attendance) ?? "";
    const allowedValues: string[] = [
      ...(hasApplyingSchools ? [AttendanceTypes.Applying] : []),
      ...(hasAttendingSchools ? [AttendanceTypes.Attending] : []),
    ];

    if (!allowedValues.includes(attendance)) {
      setAttendance(allowedValues[0] ?? AttendanceTypes.Applying);
    }
  }, [
    isElevatedSchoolAdmin,
    hasApplyingSchools,
    hasAttendingSchools,
    searchParams,
    setAttendance,
  ]);

  let tip = <></>;
  if (tabs.current.type === FormTabsTypes.DropoffForms) {
    const previousFormTemplateId = tabs.current.formTemplateId;
    const from = tabs.visibleTabs.find(
      (tab) => tab.key === previousFormTemplateId
    )?.label;
    const to = selectedNavFormTemplate?.name;

    if (from && to) {
      tip = (
        <Flex gap={4} alignItems="flex-start" marginTop={6}>
          <Tag
            width="4rem"
            color="white"
            backgroundColor="purple.500"
            alignItems="center"
            justifyContent="center"
          >
            <TagLeftIcon boxSize="0.75rem" as={RiLightbulbLine} />
            <Text>Tip</Text>
          </Tag>
          <Text>
            This list shows students from {from} who have not yet started {to}.
            There are fewer things you can do with records in this view.
          </Text>
        </Flex>
      );
    }
  }

  let formTemplateRemoteData: RD.RemoteData<Error, GQL.FormTemplateFragment>;
  let formsContent;

  switch (tabs.current.type) {
    case FormTabsTypes.DropoffForms:
      formTemplateRemoteData = remoteData.previousFormTemplateData;
      formsContent = (
        <DropoffFormsList
          enrollmentPeriodId={enrollmentPeriodId}
          limit={pagination.limit}
          offset={pagination.offset}
          dropoffFormTemplateId={selectedNavFormTemplate?.id ?? ""}
          previousFormTemplateRemoteData={formTemplateRemoteData}
          remoteData={remoteData.formData}
          onFetchMore={setPagination}
          selection={selection}
          onSelectionChange={setSelection}
          onFetchAllIds={formsList.fetchAllIds}
          onRefetch={formsList.refetch}
        />
      );
      break;

    default:
      const compositeRemoteData = RD.toTuple(
        remoteData.formData,
        remoteData.formTemplateData
      );
      formTemplateRemoteData = remoteData.formTemplateData;
      formsContent = (
        <GQLRemoteDataView remoteData={compositeRemoteData}>
          {([formList, formTemplate]) => {
            if (!formList.rows.length || !formTemplate) {
              if (filter.isApplied || search.isApplied) {
                /*
                 * When filters are applied, forms might still exist.
                 * Show the "no matching forms" view.
                 */
                return (
                  <>
                    {isElevatedSchoolAdmin && (
                      <TableHeader
                        count={0}
                        setAttendance={formsList.setAttendance}
                      />
                    )}
                    <NoDataTable
                      buttonText="Clear"
                      icon={RiClipboardLine}
                      onButtonAction={formsList.clearAll}
                      text="No forms found"
                    />
                  </>
                );
              } else {
                return selectedNavFormTemplate ? (
                  <NoForms
                    enrollmentPeriodId={enrollmentPeriodId}
                    formTemplateId={selectedNavFormTemplate.id}
                    formTemplateName={selectedNavFormTemplate.name}
                  />
                ) : null;
              }
            } else {
              return (
                <FormsList
                  enrollmentPeriodId={enrollmentPeriodId}
                  forms={formList.rows}
                  formTemplate={formTemplate}
                  limit={pagination.limit}
                  offset={pagination.offset}
                  count={formList.totalCount}
                  selection={selection}
                  onSelectionChange={setSelection}
                  onRefetch={formsList.refetch}
                  onFetchAllIds={formsList.fetchAllIds}
                  onFetchMore={setPagination}
                  onSort={formsList.sort}
                  isLoading={compositeRemoteData.isLoading()}
                  onSelectAll={formsList.selectAll}
                  tabType={tabs.current.type}
                  setAttendance={formsList.setAttendance}
                />
              );
            }
          }}
        </GQLRemoteDataView>
      );
      break;
  }

  return (
    <EnrollmentPeriodIdProvider enrollmentPeriodId={enrollmentPeriodId}>
      <VStack align="stretch" minBlockSize="100%">
        <Breadcrumb
          items={breadcrumb.form.getBreadcrumbsForList(
            organization,
            selectedNavFormTemplate,
            search.params.toString()
          )}
          mb={4}
        />
        <HStack pb={8}>
          <Heading as="h1" variant="admin">
            {selectedNavFormTemplate?.name ?? "Forms"}{" "}
            <WithUserPermissions permissions={["form_template:update"]}>
              <WithRequiredHasuraRoles
                roles={[HasuraRole.ADMIN, HasuraRole.ORG_ADMIN]}
              >
                <Tooltip
                  hasArrow
                  placement="top"
                  label="Edit form in builder, in a new tab"
                >
                  <Link
                    to={organization
                      .map((org) =>
                        Url.OrgAdmin.FormTemplateContent.edit(
                          org,
                          enrollmentPeriodId,
                          selectedNavFormTemplate?.id ?? ""
                        )
                      )
                      .withDefault("#")}
                    target="_blank"
                  >
                    <Icon
                      fontSize="lg"
                      color="primary.600"
                      as={RiExternalLinkLine}
                    />
                  </Link>
                </Tooltip>
              </WithRequiredHasuraRoles>
            </WithUserPermissions>
          </Heading>

          <Spacer />

          {selectedNavFormTemplate && (
            <WithFeatureFlags featureFlags={["async-bulk-actions"]}>
              <ActivityButton formTemplateId={selectedNavFormTemplate.id} />
            </WithFeatureFlags>
          )}

          <Box>
            <WithUserPermissions permissions={["form:update"]}>
              <ImportMenuInput
                onFormListImported={formsList.imported}
                formTemplate={selectedNavFormTemplate}
              />
            </WithUserPermissions>
          </Box>

          <WithRequiredHasuraRoles
            roles={[HasuraRole.ADMIN, HasuraRole.ORG_ADMIN]}
          >
            <WithUserPermissions permissions={["form:create"]}>
              {selectedNavFormTemplate && (
                <CreateFormsButton
                  enrollmentPeriodId={enrollmentPeriodId}
                  formTemplateId={selectedNavFormTemplate.id}
                  formTemplateName={selectedNavFormTemplate.name}
                />
              )}
            </WithUserPermissions>
          </WithRequiredHasuraRoles>
        </HStack>
        <Tabs index={tabs.currentIndex}>
          <TabList>
            {tabs.visibleTabs.map((tab) => (
              <Tab
                key={tab.key}
                value={tab.key}
                onClick={() => tabs.onChange(tab)}
              >
                {tab.type === FormTabsTypes.DropoffForms
                  ? `Not started (From: ${tab.label})`
                  : tab.label}
              </Tab>
            ))}
          </TabList>
        </Tabs>
        {tip}
        <FormListViewWithFilter searchInput={<SearchInput {...search} />}>
          {({ isFilterOpen, onFilterToggle }) =>
            filterDrawerEnabled ? (
              <>
                {formsContent}
                <ResizableDrawer
                  autosaveId="formFilterDrawerWidth"
                  isOpen={isFilterOpen}
                  onClose={onFilterToggle}
                  header="Filter"
                  bodyProps={{
                    paddingLeft: "2",
                    paddingRight: "0",
                    paddingBottom: "4",
                  }}
                >
                  <FormFilters
                    formTemplateRemoteData={formTemplateRemoteData}
                    onToggleFilter={onFilterToggle}
                    onClearFilters={filter.clear}
                    enrollmentPeriodId={enrollmentPeriodId}
                    hideHeader
                  />
                </ResizableDrawer>
              </>
            ) : (
              <ResizableFilterPanel
                autosaveId="formFilterPanelWidth"
                leftContent={formsContent}
                showRightContent={isFilterOpen}
                rightContent={
                  <FormFilters
                    formTemplateRemoteData={formTemplateRemoteData}
                    onToggleFilter={onFilterToggle}
                    onClearFilters={filter.clear}
                    enrollmentPeriodId={enrollmentPeriodId}
                    height="calc(100vh - 160px)"
                  />
                }
              />
            )
          }
        </FormListViewWithFilter>
      </VStack>
    </EnrollmentPeriodIdProvider>
  );
};
